import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, MoreVertical, Plus, Trash2, Upload, Users, Workflow } from 'lucide-react';
import { UploadCampaignFormDialog } from '@/features/campaigns/ui/upload-campaign-form-dialog';
import { usePassbook } from '@/shared/lib/passbook';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import type { Segmentation } from '@/shared/types/segmentation';
import { deleteSegmentation, duplicateSegmentation, fetchSegmentations } from '../api/use-segmentations';
import { isSegmentationStale } from '../model/segmentation-status';
import { SegmentationAiCostBadge } from './segmentation-ai-cost-badge';

export function SegmentationsPage() {
  const { idPassbook } = usePassbook();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [chooseOpen, setChooseOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: segmentations, isLoading } = useQuery({
    queryKey: ['segmentations', idPassbook],
    queryFn: () => fetchSegmentations(idPassbook),
  });

  async function handleDuplicate(id: string) {
    setPendingId(id);
    try {
      await duplicateSegmentation(id, idPassbook);
      await queryClient.invalidateQueries({ queryKey: ['segmentations', idPassbook] });
      toast.success('Audiência duplicada.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível duplicar.');
    } finally {
      setPendingId(null);
    }
  }

  async function handleDelete(segmentation: Segmentation) {
    const ok = await confirm({
      title: 'Excluir audiência',
      description: `Remover permanentemente "${segmentation.nome}"? Essa ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    setPendingId(segmentation._id);
    try {
      await deleteSegmentation(segmentation._id, idPassbook);
      await queryClient.invalidateQueries({ queryKey: ['segmentations', idPassbook] });
      toast.success('Audiência excluída.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível excluir.');
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Audiência"
        description="Crie públicos por arquivo ou do zero. O upload vira uma audiência que qualquer campanha pode usar."
        hideMetaOnMobile
        actions={
          <Button onClick={() => setChooseOpen(true)}>
            <Plus className="size-4" />
            <span className="sm:hidden">Nova</span>
            <span className="hidden sm:inline">Nova audiência</span>
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : !segmentations || segmentations.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 px-6 py-16 text-center">
          <Users className="size-8 text-muted-foreground" />
          <p className="text-sm font-medium">Nenhuma audiência criada ainda</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Envie um arquivo ou monte as condições no fluxo. O público fica disponível para as campanhas.
          </p>
          <Button onClick={() => setChooseOpen(true)}>
            <Plus className="size-4" />
            Criar primeira audiência
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {segmentations.map((segmentation) => {
            const stale = isSegmentationStale(segmentation);
            return (
              <Card
                key={segmentation._id}
                className="cursor-pointer transition-transform hover:-translate-y-0.5"
                onClick={() => navigate(`${segmentation._id}/editar`)}
              >
                <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
                  <CardTitle className="line-clamp-2">{segmentation.nome}</CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon-sm" aria-label="Mais ações" disabled={pendingId === segmentation._id}>
                        <MoreVertical className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => handleDuplicate(segmentation._id)}>
                        <Copy className="size-3.5" />
                        Duplicar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(segmentation)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="size-3.5" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-2xl font-semibold tabular-nums">
                      {(segmentation.leadCount ?? 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-muted-foreground">leads no último cálculo</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <Badge variant={stale ? 'warning' : segmentation.status === 'active' ? 'success' : 'secondary'}>
                      {stale ? 'Desatualizado' : segmentation.status === 'active' ? 'Ativa' : 'Rascunho'}
                    </Badge>
                    <SegmentationAiCostBadge usage={segmentation.aiUsage} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <Dialog open={chooseOpen} onOpenChange={setChooseOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova audiência</DialogTitle>
            <DialogDescription>O upload usa o mesmo envio de arquivo das campanhas e também grava uma audiência.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Button
              type="button"
              variant="secondary"
              className="h-auto flex-col items-start gap-2 px-4 py-4 text-left"
              onClick={() => {
                setChooseOpen(false);
                setUploadOpen(true);
              }}
            >
              <Upload className="size-4" />
              <span>
                <span className="block font-medium">Upload</span>
                <span className="mt-1 block text-xs font-normal text-muted-foreground">Arquivo do BI com serial_number.</span>
              </span>
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="h-auto flex-col items-start gap-2 px-4 py-4 text-left"
              onClick={() => navigate('nova')}
            >
              <Workflow className="size-4" />
              <span>
                <span className="block font-medium">Criar do zero</span>
                <span className="mt-1 block text-xs font-normal text-muted-foreground">Abre o fluxo de condições.</span>
              </span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <UploadCampaignFormDialog
        idPassbook={idPassbook}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ['segmentations', idPassbook] });
        }}
      />
    </div>
  );
}
